import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import { Link } from 'expo-router';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import MacWorkspacePreview from '@/components/Mac/MacWorkspacePreview';
import { MAC_WORKFLOWS } from '@/constants/macPageContent';
import { EXTENSION_STORE_URL } from '@/constants/browserExtension';

const WORK_EXAMPLES = [
  {
    label: 'Research', icon: 'globe',
    prompt: 'Compare three local venues for our launch. Make a recommendation and a budget.',
    steps: ['Read the brief and requirements', 'Compare venue websites and pricing', 'Build the recommendation and budget'],
    files: [['file-lines', 'Venue recommendation.docx'], ['table', 'Launch budget.xlsx']],
  },
  {
    label: 'Presentations', icon: 'chalkboard',
    prompt: 'Turn these launch notes into a presentation with speaker notes and a clear next step.',
    steps: ['Read your notes and supporting files', 'Shape the story and design the slides', 'Check the layout and add speaker notes'],
    files: [['file-powerpoint', 'Launch presentation.pptx'], ['file-pdf', 'Presentation handout.pdf']],
  },
  {
    label: 'Code & games', icon: 'code',
    prompt: 'Build a space arcade game with keyboard controls, a score, and a restart button.',
    steps: ['Plan the game and its controls', 'Build movement, collisions, and scoring', 'Package a playable preview and source'],
    files: [['gamepad', 'Space arcade.html'], ['file-lines', 'How to play.md']],
  },
];

const BOT_EXAMPLES = [
  {
    name: 'Research partner', image: require('@/assets/images/bots/research-analyst.png'),
    request: 'Build me a research bot that compares options and always includes its sources.',
    instructions: 'Compare the evidence. Explain the tradeoffs. Include source links and a clear recommendation.',
    job: 'Compare three newsletter platforms for my business.',
  },
  {
    name: 'Creative writer', image: require('@/assets/images/bots/executive-ghostwriter-speechwriter.png'),
    request: 'I want a writing bot that sounds like me: warm, direct, and light on jargon.',
    instructions: 'Keep the voice warm and direct. Use concrete details, short paragraphs, and a useful next step.',
    job: 'Draft our next newsletter from these project notes.',
  },
  {
    name: 'Code partner', image: require('@/assets/images/bots/full-stack-software-architect.png'),
    request: 'Make a coding bot that follows my project conventions and explains what changed.',
    instructions: 'Read the project instructions. Follow existing patterns, run relevant checks, and summarize changes.',
    job: 'Build a contact page that matches the rest of my site.',
  },
];

const FILM_SCENES = [
  { title: 'The escape', caption: 'A quiet morning. A different pace.', source: require('@/assets/images/landing-hero-lakeside.jpg') },
  { title: 'The journey', caption: 'There is a whole world out here.', source: require('@/assets/images/landing-mountain.png') },
  { title: 'The arrival', caption: 'Go a little further from ordinary.', source: require('@/assets/images/landing-hero-summit.jpg') },
];

function palette(isDark) {
  return {
    ink: isDark ? '#f3f4f3' : '#18221d',
    muted: isDark ? '#b0bab4' : '#5d6862',
    surface: isDark ? '#1c2420' : '#ffffff',
    inset: isDark ? '#252e28' : '#f3f5f2',
    rule: isDark ? '#3a453e' : '#dfe5df',
    accent: isDark ? '#88cfab' : '#0d5c47',
    band: isDark ? '#151c18' : '#f1f4ef',
  };
}

function Copy({ children, p, style }) {
  return <Text style={[{ color: p.muted, fontSize: 16, lineHeight: 26 }, style]}>{children}</Text>;
}

function FeatureLink({ href, children, p }) {
  return <Link href={href} asChild>
    <Pressable style={{ flexDirection: 'row', gap: 10, alignItems: 'center', alignSelf: 'flex-start', minHeight: 48, paddingVertical: 12 }}>
      <Text weight="semibold" style={{ color: p.accent, fontSize: 15 }}>{children}</Text>
      <Icon name="arrow-right" size={13} color={p.accent} />
    </Pressable>
  </Link>;
}

function FeatureHeading({ eyebrow, title, children, compact, p }) {
  return <View style={{ gap: 18 }}>
    <Text weight="semibold" style={{ color: p.accent, fontSize: 12, lineHeight: 19, letterSpacing: 1.6, textTransform: 'uppercase' }}>{eyebrow}</Text>
    <Text tag="h2" weight="bold" style={{ color: p.ink, fontSize: compact ? 34 : 48, lineHeight: compact ? 39 : 53, letterSpacing: compact ? -1 : -1.7, whiteSpace: 'pre-line' }}>{title}</Text>
    <Copy p={p}>{children}</Copy>
  </View>;
}

function Points({ items, p }) {
  return <View style={{ gap: 14, marginTop: 24, marginBottom: 14 }}>
    {items.map(([icon, text]) => <View key={text} style={styles.row}>
      <Icon name={icon} size={16} color={p.accent} />
      <Copy p={p} style={{ flex: 1, fontSize: 14, lineHeight: 22 }}>{text}</Copy>
    </View>)}
  </View>;
}

function Window({ title, p, children }) {
  return <View style={{ borderRadius: 18, borderWidth: 1, borderColor: p.rule, backgroundColor: p.surface, overflow: 'hidden', boxShadow: '0 18px 55px rgba(0,0,0,0.08)' }}>
    <View style={[styles.row, { padding: 16, borderBottomWidth: 1, borderColor: p.rule }]}>
      <View aria-hidden style={{ flexDirection: 'row', gap: 5 }}>
        {['#ed6a5e', '#f4bd4f', '#62c554'].map(color => <View key={color} style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />)}
      </View>
      <Text size="xs" weight="medium" style={{ color: p.muted, flex: 1, textAlign: 'right' }}>{title}</Text>
    </View>
    {children}
  </View>;
}

function Choice({ selected, onPress, children, label, p }) {
  return <Pressable accessibilityRole="button" accessibilityLabel={label} aria-pressed={selected} onPress={onPress}
    style={({ hovered }) => ({ minHeight: 44, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: selected ? p.accent : p.rule, backgroundColor: selected || hovered ? p.inset : p.surface, justifyContent: 'center' })}>
    <Text weight={selected ? 'semibold' : 'medium'} style={{ color: selected ? p.accent : p.muted, fontSize: 13 }}>{children}</Text>
  </Pressable>;
}

function WorkPreview({ p, compact }) {
  const [selected, setSelected] = useState(0);
  const example = WORK_EXAMPLES[selected];
  return <Window title="Work · Example workflow" p={p}>
    <View style={{ padding: compact ? 18 : 26, gap: 22 }}>
      <View accessibilityRole={safeAccessibilityRole('group')} accessibilityLabel="Choose a Work example" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {WORK_EXAMPLES.map((item, index) => <Choice key={item.label} label={`Preview ${item.label} workflow`} selected={selected === index} onPress={() => setSelected(index)} p={p}>{item.label}</Choice>)}
      </View>
      <View style={{ backgroundColor: p.inset, borderRadius: 14, padding: 16 }}>
        <Text style={{ color: p.ink, fontSize: 15, lineHeight: 24 }}>{example.prompt}</Text>
      </View>
      <View style={{ gap: 16 }}>
        {example.steps.map((step, index) => <View key={step} style={styles.row}>
          <View style={{ width: 24, height: 24, borderRadius: 12, borderWidth: 1, borderColor: p.rule, justifyContent: 'center', alignItems: 'center' }}><Text size="xs" style={{ color: p.accent }}>{index + 1}</Text></View>
          <Text style={{ flex: 1, color: p.muted, fontSize: 14, lineHeight: 21 }}>{step}</Text>
        </View>)}
      </View>
      <View style={{ borderTopWidth: 1, borderColor: p.rule, paddingTop: 20, gap: 10 }}>
        <Text weight="semibold" style={{ color: p.ink, fontSize: 13, marginBottom: 3 }}>Finished files. Ready for your review.</Text>
        {example.files.map(([icon, name]) => <View key={name} style={[styles.row, { backgroundColor: p.inset, borderRadius: 10, padding: 14 }]}>
          <Icon name={icon} size={19} color={p.accent} />
          <Text style={{ color: p.ink, fontSize: 13, flex: 1 }}>{name}</Text>
          <Icon name="check" size={12} color={p.accent} />
        </View>)}
      </View>
    </View>
  </Window>;
}

function BotPreview({ p, compact }) {
  const [selected, setSelected] = useState(0);
  const bot = BOT_EXAMPLES[selected];
  return <Window title="Build a bot · Example setup" p={p}>
    <View style={{ padding: compact ? 18 : 26, gap: 20 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {BOT_EXAMPLES.map((item, index) => <Choice key={item.name} label={`Preview ${item.name} bot`} selected={index === selected} onPress={() => setSelected(index)} p={p}>{item.name}</Choice>)}
      </View>
      <View style={{ backgroundColor: p.inset, padding: 15, borderRadius: 14 }}><Copy p={p} style={{ color: p.ink, fontSize: 14, lineHeight: 23 }}>{bot.request}</Copy></View>
      <View style={{ borderWidth: 1, borderColor: p.rule, borderRadius: 14, padding: 20, gap: 16 }}>
        <View style={styles.row}>
          <Image source={bot.image} contentFit="contain" style={{ width: 76, height: 76 }} accessibilityLabel={`${bot.name} avatar`} />
          <View style={{ flex: 1, gap: 5 }}><Text weight="bold" style={{ color: p.ink, fontSize: 19 }}>{bot.name}</Text><Text size="xs" style={{ color: p.muted }}>Your instructions. Your preferred model.</Text></View>
        </View>
        <Copy p={p} style={{ fontSize: 14, lineHeight: 23 }}>{bot.instructions}</Copy>
        <View style={{ borderTopWidth: 1, borderColor: p.rule, paddingTop: 16, gap: 7 }}>
          <Text size="xs" weight="semibold" style={{ color: p.accent }}>GIVE IT A JOB</Text>
          <Text style={{ color: p.ink, fontSize: 14, lineHeight: 22 }}>{bot.job}</Text>
        </View>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 18 }}>
        {['Quick Chat', 'Work jobs', 'Scheduled tasks'].map(label => <Text key={label} size="xs" style={{ color: p.muted }}>{label}</Text>)}
      </View>
    </View>
  </Window>;
}

function FilmPreview({ compact }) {
  const [selected, setSelected] = useState(0);
  const scene = FILM_SCENES[selected];
  return <View style={{ backgroundColor: '#171d1a', borderRadius: 18, padding: compact ? 14 : 20, gap: 16, boxShadow: '0 18px 55px rgba(0,0,0,0.14)' }}>
    <View style={[styles.row, { justifyContent: 'space-between' }]}>
      <Text size="xs" weight="medium" style={{ color: '#d9e2db' }}>Weekend escape · Example storyboard</Text>
      <Icon name="film" size={15} color="#b8c7be" />
    </View>
    <View style={{ aspectRatio: 16 / 10, borderRadius: 10, overflow: 'hidden', justifyContent: 'flex-end' }}>
      <Image source={scene.source} contentFit="cover" style={StyleSheet.absoluteFillObject} accessibilityLabel={`Illustrative film still: ${scene.title}`} />
      <View style={{ padding: compact ? 18 : 26, backgroundColor: 'rgba(0,0,0,0.56)', gap: 7 }}>
        <Text size="xs" style={{ color: '#d9e2db', letterSpacing: 1.2 }}>SCENE 0{selected + 1} / {scene.title.toUpperCase()}</Text>
        <Text weight="semibold" style={{ color: '#ffffff', fontSize: compact ? 22 : 28, lineHeight: compact ? 28 : 34 }}>{scene.caption}</Text>
      </View>
    </View>
    <View style={{ flexDirection: 'row', gap: 8 }}>
      {FILM_SCENES.map((item, index) => <Pressable key={item.title} accessibilityRole="button" accessibilityLabel={`Preview film scene ${index + 1}: ${item.title}`} aria-pressed={selected === index} onPress={() => setSelected(index)}
        style={{ flex: 1, minWidth: 0, borderRadius: 8, padding: 3, borderWidth: 2, borderColor: selected === index ? '#b9dec9' : '#3b4941' }}>
        <Image source={item.source} contentFit="cover" accessible={false} style={{ width: '100%', aspectRatio: 2, borderRadius: 4 }} />
        <Text size="xs" style={{ color: '#e5ece7', paddingVertical: 8, textAlign: 'center' }}>0{index + 1} · {item.title}</Text>
      </Pressable>)}
    </View>
    {[['microphone', 'Voiceover', '#444e66'], ['music', 'Soundtrack', '#3a5747']].map(([icon, label, color]) => <View key={label} style={styles.row}>
      <Icon name={icon} size={13} color="#c9d3cc" />
      <Text size="xs" style={{ color: '#c9d3cc', width: 74 }}>{label}</Text>
      <View style={{ flex: 1, height: 23, borderRadius: 5, backgroundColor: color, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly' }}>
        {Array.from({ length: 24 }, (_, index) => <View key={index} style={{ width: 2, height: 5 + (index * 7 % 14), backgroundColor: '#bdcbc3', opacity: 0.6 }} />)}
      </View>
    </View>)}
    <Text size="xs" style={{ color: '#b8c7be', textAlign: 'center', marginTop: 4 }}>Script → Stills → Clips → Review → Final assembly</Text>
  </View>;
}

function BrowserPreview({ p, compact }) {
  return <Window title="Chrome / Brave · Illustrative side chat" p={p}>
    <View style={{ flexDirection: compact ? 'column' : 'row' }}>
      <View style={{ flex: 1, minWidth: 0, padding: 24, gap: 18, backgroundColor: p.inset }}>
        <Text size="xs" style={{ color: p.muted }}>THE WEEKEND JOURNAL</Text>
        <Text weight="bold" style={{ color: p.ink, fontSize: 27, lineHeight: 32, letterSpacing: -0.7 }}>A little more time outside.</Text>
        <Image source={FILM_SCENES[1].source} contentFit="cover" style={{ width: '100%', aspectRatio: 1.8, borderRadius: 8 }} accessibilityLabel="Mountain landscape in an example webpage" />
        <Copy p={p} style={{ fontSize: 13, lineHeight: 21 }}>Three trails, a picnic by the lake, and a weekend worth planning.</Copy>
      </View>
      <View style={{ flex: 1, minWidth: 0, padding: 22, gap: 19, borderLeftWidth: compact ? 0 : 1, borderTopWidth: compact ? 1 : 0, borderColor: p.rule }}>
        <View style={styles.row}><Icon name="comment" size={16} color={p.accent} /><Text weight="semibold" style={{ color: p.ink, fontSize: 15 }}>Gab AI</Text></View>
        <View style={{ borderWidth: 1, borderColor: p.rule, borderRadius: 9, padding: 10 }}><Text size="xs" style={{ color: p.muted }}>Page text added to your draft</Text></View>
        <View style={{ padding: 13, backgroundColor: p.inset, borderRadius: 12 }}><Text style={{ color: p.ink, fontSize: 14, lineHeight: 22 }}>Turn this into a two-day itinerary. Leave time for a relaxed lunch.</Text></View>
        <View style={{ gap: 14 }}>
          {[['Saturday', 'A morning hike, lunch by the lake, and a quiet afternoon.'], ['Sunday', 'Take the scenic route and stop at the overlook on the way home.']].map(([day, copy]) => <View key={day} style={{ gap: 5 }}><Text weight="semibold" style={{ color: p.ink, fontSize: 13 }}>{day}</Text><Text style={{ color: p.muted, fontSize: 13, lineHeight: 21 }}>{copy}</Text></View>)}
        </View>
      </View>
    </View>
  </Window>;
}

export default function ChangelogHighlights({ isDark, compact }) {
  const p = palette(isDark);
  const section = { width: '100%', alignItems: 'center', paddingHorizontal: compact ? 20 : 40, paddingVertical: compact ? 64 : 100 };
  const split = { width: '100%', maxWidth: 1120, flexDirection: compact ? 'column' : 'row', alignItems: compact ? 'stretch' : 'center', gap: compact ? 32 : 64 };
  const copy = { flex: compact ? undefined : 0.85, minWidth: 0 };
  const visual = { flex: compact ? undefined : 1.15, minWidth: 0 };

  return <>
    <View nativeID="new-work" style={[section, { backgroundColor: p.band }]}>
      <View style={split}>
        <View style={copy}>
          <FeatureHeading p={p} compact={compact} eyebrow="New · Gab AI Work" title={'Give it a job.\nGet the finished work.'}>
            Research, write, build, and create in one conversation. Work turns your brief into a plan, follows the steps, and delivers files you can actually use.
          </FeatureHeading>
          <Points p={p} items={[
            ['files', 'Documents, slides, spreadsheets, websites, games, and media.'],
            ['brain', 'Choose Fast, Standard, or High thinking power.'],
            ['list-check', 'Set a /goal, follow progress, and pause or refine along the way.'],
          ]} />
          <FeatureLink p={p} href="/docs/work">Meet Work mode</FeatureLink>
          <Text size="xs" style={{ color: p.muted }}>Requires a plan with Work access.</Text>
        </View>
        <View style={visual}><WorkPreview p={p} compact={compact} /></View>
      </View>
    </View>

    <View nativeID="new-mac" style={section}>
      <View style={{ width: '100%', maxWidth: 1120, gap: 36 }}>
        <View style={split}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <FeatureHeading p={p} compact={compact} eyebrow="New · Gab AI for Mac + Remote" title={'Your projects.\nNow with a helping hand.'}>
              Bring Work into the folders, browser tabs, and supported apps you choose on your Mac. Browse files, edit code, run approved checks, and review the changes in one place.
            </FeatureHeading>
          </View>
          <View style={{ flex: compact ? undefined : 0.8, minWidth: 0 }}>
            <Points p={p} items={[
              ['folder-open', 'Connect a project folder and choose its access.'],
              ['code-branch', 'Review Git diffs and restore supported saved edits.'],
              ['desktop', 'Native controls, task notifications, and in-app updates.'],
            ]} />
            <FeatureLink p={p} href="/mac">Explore the Mac app</FeatureLink>
          </View>
        </View>
        <MacWorkspacePreview workflow={MAC_WORKFLOWS[0]} />
        <View style={{ backgroundColor: p.inset, borderRadius: 18, padding: compact ? 22 : 30, flexDirection: compact ? 'column' : 'row', alignItems: compact ? 'flex-start' : 'center', gap: 24 }}>
          <View style={{ width: 52, height: 62, borderRadius: 13, backgroundColor: p.surface, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: p.rule }}><Icon name="mobile" size={29} color={p.ink} /></View>
          <View style={{ flex: 1, gap: 7 }}>
            <Text tag="h3" weight="bold" style={{ color: p.ink, fontSize: 22, lineHeight: 28 }}>Away from your desk? Take the project with you.</Text>
            <Copy p={p} style={{ fontSize: 14, lineHeight: 23 }}>Pair your iPhone or Android device to work in shared Mac folders and hand tasks between devices. Keep your Mac awake, online, and running Gab AI.</Copy>
          </View>
          <FeatureLink p={p} href="/docs/remote">Set up Remote</FeatureLink>
        </View>
      </View>
    </View>

    <View nativeID="new-bots" style={[section, { backgroundColor: p.band }]}>
      <View style={split}>
        <View style={copy}>
          <FeatureHeading p={p} compact={compact} eyebrow="New · Conversational bot builder" title={'Your kind of helper.\nBuilt by chatting.'}>
            Tell Gab AI what you need help with. Shape your bot’s role, instructions, and personality in conversation, then keep it ready for the next assignment.
          </FeatureHeading>
          <Points p={p} items={[
            ['sliders', 'Choose the models it uses for Chat and Work.'],
            ['comment', 'Mention a bot or make it your default assistant.'],
            ['calendar', 'Start a quick chat, hand over a job, or schedule a recurring task.'],
          ]} />
          <FeatureLink p={p} href="/agents">Find your next bot</FeatureLink>
        </View>
        <View style={visual}><BotPreview p={p} compact={compact} /></View>
      </View>
    </View>

    <View nativeID="new-video" style={section}>
      <View style={split}>
        <View style={copy}>
          <FeatureHeading p={p} compact={compact} eyebrow="New · Work video studio" title={'From the first idea\nto the final frame.'}>
            Make a film with a story, scenes, voiceover, and music. Work brings the pieces together in a studio where you can review each stage and ask for another take.
          </FeatureHeading>
          <Points p={p} items={[
            ['image', 'Review reference stills before moving into animation.'],
            ['microphone', 'Refine clips, narration, and music before final assembly.'],
            ['scissors', 'Upload footage for transcript-guided cuts, captions, and edits.'],
          ]} />
          <FeatureLink p={p} href="/docs/work-videos">Explore the video workflow</FeatureLink>
        </View>
        <View style={visual}><FilmPreview compact={compact} /></View>
      </View>
    </View>

    <View nativeID="new-browser" style={[section, { backgroundColor: p.band }]}>
      <View style={split}>
        <View style={copy}>
          <FeatureHeading p={p} compact={compact} eyebrow="New · Browser extension" title={'The page you’re on.\nA little help beside it.'}>
            Open Gab AI beside a webpage in Chrome or Brave. Add the page or selected text to your draft, then ask a question without losing your place.
          </FeatureHeading>
          <Points p={p} items={[
            ['table-columns', 'Side chat works on Mac, Windows, and Linux.'],
            ['globe', 'Connect the Mac app for Work in approved browser tabs.'],
            ['lock', 'Use your existing website sign-ins and stay in control of access.'],
          ]} />
          <FeatureLink p={p} href={EXTENSION_STORE_URL}>Install the extension</FeatureLink>
          <Text size="xs" style={{ color: p.muted }}>Chrome Web Store · Chrome and Brave</Text>
        </View>
        <View style={visual}><BrowserPreview p={p} compact={compact} /></View>
      </View>
    </View>
  </>;
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
});
